import type { ReactNode } from 'react'
import { Check, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useCrm2Accounts } from './api'
import { fieldClass } from './shared'

export const SIZES = ['1-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'] as const
export const INDUSTRIES = ['BPO / Contact center', 'Staffing / Recruitment', 'Healthcare', 'Retail', 'Logistics', 'Hospitality', 'Insurance', 'Banking / Finance', 'Telecom', 'Technology', 'Manufacturing', 'Education'] as const
export const SOURCES = ['Demo request form', 'Referral', 'Event', 'Outbound', 'LinkedIn', 'Website chat', 'Partner', 'Existing client'] as const

/** Suggestions for the free-text fields: typing anything else is fine. */
export function Suggestions() {
  return (
    <>
      <datalist id="crm2-industries">
        {INDUSTRIES.map((x) => (
          <option key={x} value={x} />
        ))}
      </datalist>
      <datalist id="crm2-sources">
        {SOURCES.map((x) => (
          <option key={x} value={x} />
        ))}
      </datalist>
    </>
  )
}

export function SizeSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select aria-label="Company size" value={value} onChange={(e) => onChange(e.target.value)} className={fieldClass}>
      <option value="">Not known yet</option>
      {SIZES.map((s) => (
        <option key={s} value={s}>
          {s} people
        </option>
      ))}
    </select>
  )
}

/** Which account this company belongs to, for a region or department of a bigger enterprise. */
export function MainAccountSelect({ value, onChange, exclude }: { value: string; onChange: (v: string) => void; exclude?: string }) {
  const all = useCrm2Accounts('').data ?? []
  return (
    <select aria-label="Main account" value={value} onChange={(e) => onChange(e.target.value)} className={fieldClass}>
      <option value="">None, this is the main account</option>
      {all
        .filter((a) => a.id !== exclude)
        .map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
    </select>
  )
}

/** A titled group of fields, with the part of the account page it fills. */
export function FormGroup({ title, fills, children }: { title: string; fills: string; children: ReactNode }) {
  return (
    <fieldset className="grid gap-3 rounded-lg border border-border-soft p-3 sm:grid-cols-2">
      <legend className="px-1 text-sm font-semibold">
        {title} <span className="font-normal text-muted-foreground">· {fills}</span>
      </legend>
      {children}
    </fieldset>
  )
}

/** What the account page will show once this is saved, ticked as it is filled in. Required items are marked. */
export function Readiness({ items }: { items: Array<{ label: string; done: boolean; required?: boolean }> }) {
  const done = items.filter((i) => i.done).length
  return (
    <aside className="grid content-start gap-2 self-start rounded-lg bg-muted/60 p-3 text-sm md:sticky md:top-0" aria-label="What the account page will show">
      <div className="font-semibold">The account page will show</div>
      <div className="text-xs text-muted-foreground">
        {done} of {items.length} filled in
      </div>
      <ul className="grid gap-1.5">
        {items.map((i) => (
          <li key={i.label} className={cn('flex items-center gap-2', i.done ? 'text-foreground' : 'text-muted-foreground')}>
            {i.done ? <Check className="size-4 shrink-0 text-success" /> : <Circle className="size-4 shrink-0 text-text-muted" />}
            <span>
              {i.label}
              {i.required && !i.done ? <span className="ml-1 text-xs font-medium text-danger">needed</span> : null}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-tiny text-text-muted">Anything left empty can be added later from the account page.</p>
    </aside>
  )
}
